import { DSButton } from '@repo/design-system/index'
import { cloneDeep, shuffle } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { n_ai_tutor } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import { ActivityScreen, TextBox, TextBoxWord, Word } from '../Common'

const FillinActivity = ({
  content,
  nextActivity,
  addFail,
  retry,
  idx,
}: {
  content: Word[]
  nextActivity: () => void
  addFail: () => void
  retry?: boolean
  idx?: any
}) => {
  const { t } = useTranslation()
  const [solved, setSolved] = useState(null)
  const [fill, setFill] = useState([] as Word[])
  const [usedChoices, setUsedChoices] = useState(new Set())
  const choices = useMemo(() => {
    setFill(
      cloneDeep(content).map((word) => {
        if (word.keyword) return { ...word, word: '', empty: true }
        return word
      })
    )
    const shuffled = shuffle(
      content.filter((word) => word.keyword).map((word) => ({ word: word.word, selected: false }))
    )
    setUsedChoices(new Set())
    return shuffled
  }, [content])

  const [judged, setJudged] = useState(false)

  const words = useMemo(() => {
    if (!judged)
      return fill.map((word) => {
        return {
          keyword: word.keyword,
          word: word.word,
          correct: null,
          separator: word.separator,
          empty: word.empty,
          ns: word.ns,
        }
      })
    else {
      return fill.map((word, index) => {
        return {
          keyword: word.keyword,
          word: word.word,
          empty: word.empty,
          correct: word.keyword ? content[index].word === word.word : null,
          separator: word.separator,
          ns: word.ns,
        }
      })
    }
  }, [fill, judged])

  const judgeable = useMemo(() => words.every((word) => !word.empty), [words])

  const fillNow = useMemo(() => {
    for (let i = 0; i < fill.length; i++) {
      if (fill[i].empty) return i
    }
    return fill.length
  }, [fill])

  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (judged) {
      if (solved === 'done') return
      if (words.some((word) => word.correct === false)) {
        setSolved('fail')
        if (!failed) {
          addFail()
          setFailed(true)
        }
      } else {
        setSolved('done')
      }
    }
  }, [words, judged, failed])

  // const fillWord = (index: number) => () => {
  //   if (usedChoices.has(index)) return
  //   if (fillNow === -1) return
  //   const newFill = cloneDeep(fill)
  //   newFill[fillNow].word = choices[index].word
  //   newFill[fillNow].idx = index
  //   newFill[fillNow].empty = false
  //   setFill(newFill)
  //   setUsedChoices(new Set([...usedChoices, index]))
  // }

  const fillWord = (index: number) => () => {
    if (usedChoices.has(index)) return
    if (fillNow === -1) return

    setFill((prev) => {
      const newFill = [...prev]
      newFill[fillNow] = { ...newFill[fillNow], word: choices[index].word, idx: index, empty: false }
      return newFill
    })
    setUsedChoices((prev) => new Set(prev).add(index))
  }

  const cancelFill = (idx: number) => {
    if (solved) return
    if (!fill[idx].keyword || fill[idx].empty) return

    const newFill = cloneDeep(fill)
    newFill[idx].word = ''
    newFill[idx].empty = true
    delete newFill[idx].idx // idx 속성이 있다면 제거

    setFill(newFill)

    // usedChoices에서 해당 단어의 선택을 제거
    const choiceIndex = choices.findIndex((choice) => choice.word === fill[idx].word)
    if (choiceIndex !== -1) {
      const newUsedChoices = new Set(usedChoices)
      newUsedChoices.delete(choiceIndex)
      setUsedChoices(newUsedChoices)
    }
  }

  return (
    <ActivityScreen
      title={t(n_ai_tutor.study_activity.three_title)}
      solved={solved}
      explanation={
        solved === 'fail' && (
          <div className="break-words break-normal">
            {t(n_ai_tutor.study_activity.fb_body4)}
            {': '}
            {words.map((w, i) => (
              <span key={i} className={`${w.keyword ? 'underline' : ''} ${w.ns ? 'mr-[4px]' : ''}`}>
                {content[i].word}
              </span>
            ))}
          </div>
        )
      }
      nextActivity={nextActivity}
      retry={retry}
      idx={idx}
    >
      <div className="flex flex-row gap-[12px] items-start justify-center flex-wrap content-start self-stretch flex-1">
        <TextBox words={words} blind fixed onClick={(idx) => cancelFill(idx)} isKeyboardMode={false} />
        <div className="flex flex-row gap-[12px] items-start justify-center flex-wrap content-start self-stretch Title1-20Bd">
          {choices.map((c, i) => {
            if (usedChoices.has(i)) return null // 렌더링 안 함
            return (
              <div key={i} onClick={fillWord(i)} className="flex flex-row justify-center items-center">
                <TextBoxWord word={c.word} keyword background="bg-gray-50" isKeyboardMode={false} />
              </div>
            )
          })}
        </div>
      </div>
      {!solved && (
        <div className="w-full mb-[12px]">
          <DSButton disabled={!judgeable} onClick={() => judgeable && setJudged(true)}>
            <DSButton.Label>{t(n_ai_tutor.study_activity.continue_btn)}</DSButton.Label>
          </DSButton>
        </div>
      )}
    </ActivityScreen>
  )
}

export default FillinActivity

// import { DSButton } from '@repo/design-system/index'
// import { shuffle } from 'lodash'
// import { useCallback, useMemo, useRef, useState } from 'react'
// import { n_ai_tutor } from '../../../../modules/i18n/strings/auto/wording'
// import useTranslation from '../../../../modules/i18n/useTranslation'
// import { ActivityScreen, TextBoxWord, Word } from '../Common'

// type Props = {
//   content: Word[]
//   nextActivity: () => void
//   addFail: () => void
//   retry?: boolean
//   idx?: number
// }

// const FillinActivity = ({ content, nextActivity, addFail, retry, idx }: Props) => {
//   const { t } = useTranslation()

//   const initialFill = content.map((w) => (w.keyword ? null : w.word))
//   const [fill, setFill] = useState<(string | null)[]>(initialFill)
//   const [judged, setJudged] = useState(false)
//   const [usedChoices, setUsedChoices] = useState(new Set<number>())

//   const choicesRef = useRef<string[]>(shuffle(content.filter((w) => w.keyword).map((w) => w.word)))
//   const choices = choicesRef.current

//   const isFilled = useMemo(() => {
//     return content.every((w, i) => (w.keyword ? !!fill[i] : true))
//   }, [fill, content])

//   const handleChoiceClick = useCallback(
//     (word: string, index: number) => {
//       if (usedChoices.has(index)) return
//       const targetIdx = content.findIndex((w, i) => w.keyword && !fill[i])
//       if (targetIdx === -1) return

//       const updated = [...fill]
//       updated[targetIdx] = word
//       setFill(updated)

//       const newUsed = new Set(usedChoices)
//       newUsed.add(index)
//       setUsedChoices(newUsed)
//     },
//     [fill, usedChoices, content]
//   )

//   const handleCancel = useCallback(
//     (index: number) => {
//       if (!content[index].keyword || !fill[index]) return

//       const word = fill[index]
//       const updated = [...fill]
//       updated[index] = null
//       setFill(updated)

//       const wordIdx = choices.findIndex((w) => w === word)
//       if (wordIdx >= 0) {
//         const newUsed = new Set(usedChoices)
//         newUsed.delete(wordIdx)
//         setUsedChoices(newUsed)
//       }
//     },
//     [fill, usedChoices, content, choices]
//   )

//   const handleSubmit = () => {
//     if (!isFilled) return

//     setJudged(true)

//     const anyWrong = content.some((w, i) => w.keyword && fill[i] !== w.word)

//     alert(anyWrong)
//     if (anyWrong) addFail()
//   }

//   return (
//     <ActivityScreen
//       title={t(n_ai_tutor.study_activity.three_title)}
//       solved={judged ? 'done' : null}
//       nextActivity={nextActivity}
//       retry={retry}
//       idx={idx}
//     >
//       <div className="flex flex-wrap gap-2 mb-4">
//         {content.map((w, i) => {
//           const word = fill[i] ?? ''
//           const isCorrect = judged && w.keyword ? word === w.word : null
//           return (
//             <TextBoxWord
//               key={i}
//               word={word}
//               keyword={w.keyword}
//               correct={isCorrect}
//               background="bg-gray-50"
//               onClick={() => handleCancel(i)}
//             />
//           )
//         })}
//       </div>
//       {/* 선택지 */}
//       {!judged && (
//         <div className="flex flex-wrap gap-2 mb-6">
//           {choices.map((word, i) =>
//             usedChoices.has(i) ? null : (
//               <TextBoxWord
//                 key={i}
//                 word={word}
//                 keyword
//                 background="bg-gray-100"
//                 onClick={() => handleChoiceClick(word, i)}
//               />
//             )
//           )}
//         </div>
//       )}
//       {/* 제출 버튼 */}
//       {!judged && (
//         <div className="w-full">
//           <DSButton disabled={!isFilled} onClick={handleSubmit}>
//             <DSButton.Label>{t(n_ai_tutor.study_activity.continue_btn)}</DSButton.Label>
//           </DSButton>
//         </div>
//       )}
//     </ActivityScreen>
//   )
// }

// export default FillinActivity
